import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Field } from "@/pages/admin/EmployeesPage";
import { Plus, Pencil, Trash2, X, Loader2, Briefcase } from "lucide-react";

const empty = { name: "", contact: "", email: "", phone: "", address: "" };

export default function ClientesPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/clientes").then((r) => setList(r.data)).finally(() => setLoading(false));
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/clientes/${editing.id}`, form);
      else await api.post("/clientes", form);
      toast.success(editing ? "Cliente atualizado" : "Cliente criado");
      setModal(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminar ${c.name}?`)) return;
    await api.delete(`/clientes/${c.id}`);
    toast.success("Cliente eliminado");
    load();
  };

  return (
    <div data-testid="clientes-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Clientes</h1>
          <p className="text-muted-foreground">{list.length} cliente(s).</p>
        </div>
        <button
          data-testid="add-cliente-button"
          onClick={() => {
            setEditing(null);
            setForm(empty);
            setModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-[#F5B05E] transition-colors"
        >
          <Plus className="w-4 h-4" /> Novo cliente
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-7 h-7 text-primary animate-spin" />
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((c) => (
            <div key={c.id} data-testid={`cliente-card-${c.id}`} className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-lg bg-primary/15 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold leading-tight">{c.name}</p>
                  <p className="text-xs text-muted-foreground">{c.contact || "—"}</p>
                </div>
              </div>
              <div className="text-sm text-muted-foreground space-y-1">
                {c.email && <p>{c.email}</p>}
                {c.phone && <p className="font-data">{c.phone}</p>}
                {c.address && <p>{c.address}</p>}
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  data-testid={`edit-cliente-${c.id}`}
                  onClick={() => {
                    setEditing(c);
                    setForm({ ...empty, ...c });
                    setModal(true);
                  }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-secondary text-sm hover:bg-secondary/70 transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" /> Editar
                </button>
                <button
                  data-testid={`delete-cliente-${c.id}`}
                  onClick={() => remove(c)}
                  className="px-3 py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setModal(false)} />
          <form onSubmit={save} data-testid="cliente-form" className="relative bg-card border border-border rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-xl font-bold">{editing ? "Editar cliente" : "Novo cliente"}</h2>
              <button type="button" onClick={() => setModal(false)}>
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
            <div className="space-y-3">
              <Field label="Nome" testid="cli-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
              <Field label="Pessoa de contacto" testid="cli-contact" value={form.contact} onChange={(v) => setForm({ ...form, contact: v })} />
              <Field label="Email" testid="cli-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Field label="Telefone" testid="cli-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              <Field label="Morada" testid="cli-address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
            </div>
            <button data-testid="save-cliente-button" disabled={saving} className="w-full mt-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-[#F5B05E] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
